import { FilterFn, getFilteredRowModel } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";

import { TableDesktopProps } from "../../Table";

export const REPORT_START_DATE = "2024-01-01";

export const formatNumber = (num: number) => {
  const parts = num.toString().split(".");

  const integerPart = parseInt(parts[0], 10).toLocaleString("en-US");

  return `${integerPart}`;
};

export const getUniqValuesByField = <T extends Record<string, any>>(
  data: T[] | null,
  key: keyof T,
): any[] => (data ? Array.from(new Set(data.map((item) => item[key]))) : []);

export const CurrencySymbols = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  RUB: "₽",
  UAH: "₴",
  INR: "₹",
  KRW: "₩",
  AUD: "A$",
  CAD: "C$",
  CHF: "CHF",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  PLN: "zł",
  BRL: "R$",
  MXN: "MX$",
  ZAR: "R",
  HKD: "HK$",
  SGD: "S$",
  NZD: "NZ$",
} as const;

export type CurrencyCodeType = keyof typeof CurrencySymbols;

export enum CurrencyCodeEnum {
  USD = "USD",
  EUR = "EUR",
  GBP = "GBP",
  JPY = "JPY",
  CNY = "CNY",
  RUB = "RUB",
  UAH = "UAH",
  INR = "INR",
  KRW = "KRW",
  AUD = "AUD",
  CAD = "CAD",
  CHF = "CHF",
  SEK = "SEK",
  NOK = "NOK",
  DKK = "DKK",
  PLN = "PLN",
  BRL = "BRL",
  MXN = "MXN",
  ZAR = "ZAR",
  HKD = "HKD",
  SGD = "SGD",
  NZD = "NZD",
  CZK = "CZK",
  TRY = "TRY",
}

export enum ClientTransactionTypes {
  Deposit = "deposit",
  Withdrawal = "withdrawal",
}

export enum ClientTransactionStatuses {
  Approved = "Approved",
  Failed = "Failed",
  Pending = "Pending",
}

export type ClientTransactionStatusesType =
  keyof typeof ClientTransactionStatuses;
export type ClientTransactionStatusColors = {
  main: string;
  bg: string;
};

export const ClientTransactionStatusColors: Record<
  ClientTransactionStatusesType,
  ClientTransactionStatusColors
> = {
  [ClientTransactionStatuses.Approved]: {
    main: "text-text-success",
    bg: "bg-background-success",
  },
  [ClientTransactionStatuses.Failed]: {
    main: "text-text-base-failed",
    bg: "bg-background-failed",
  },
  [ClientTransactionStatuses.Pending]: {
    main: "text-text-base-pending",
    bg: "bg-background-pending",
  },
};

export const formatCurrencyWithoutSymbolV2 = (
  num = 0,
  currency = "USD",
  currencySymbol = "$",
  language: string,
  round = false,
  hasSign = true,
  hasCurrencyCode = false,
) => {
  const value = num
    .toLocaleString(language, {
      style: "currency",
      currency: currency,
      currencyDisplay: "code",
      maximumFractionDigits: round ? 0 : 2,
    })
    .replace(currency, "")
    .trim();
  const currencyCode = hasCurrencyCode ? currency : "";

  return num < 0
    ? `${hasSign ? "-" : ""}${currencyCode}${currencySymbol}${value.slice(2)}`
    : `${hasSign ? "+" : ""}${currencyCode}${currencySymbol}${value}`;
};

export const formatNumberV2 = (
  num?: string | number,
  language?: string,
  round?: boolean,
) => {
  if (!num) {
    return "0.00";
  }

  let val = typeof num === "string" ? +num : num;

  const value = val
    .toLocaleString(
      language,
      round
        ? {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        : undefined,
    )
    .trim();

  return value;
};

export const getPlatformColor = (
  key: "standard" | "razor",
  type: "fill" | "bg",
) => {
  const fillColors = {
    standard: "fill-blue-200",
    razor: "fill-blue-400",
  };

  const bgColors = {
    standard: "bg-blue-200",
    razor: "bg-blue-400",
  };

  return type === "fill" ? fillColors[key] : bgColors[key];
};

export const getAnalyticsSelectorValue = (
  rebateAccountIDs: (string | number)[],
  selectedRebateAccountIDs: string[],
): `${string} items selected` =>
  `${selectedRebateAccountIDs?.length === rebateAccountIDs.length ? "All" : selectedRebateAccountIDs?.length} items selected`;

export const customGlobalFilter: FilterFn<any> = (
  row,
  columnId,
  filterValue: string,
) => {
  const search = filterValue.toLowerCase();
  const rowValue = row.getValue<string | null>(columnId);
  if (!rowValue) return false;

  return Boolean(rowValue?.toString()?.toLowerCase()?.includes(search));
};

type filterPropsType = {
  globalFilter: string;
  setGlobalFilter: Dispatch<SetStateAction<string>>;
};
type InitFilterProps = (
  props: filterPropsType,
) => Pick<
  TableDesktopProps<any>,
  | "getFilteredRowModel"
  | "onGlobalFilterChange"
  | "globalFilterFn"
  | "globalFilter"
  | "filterFns"
>;

export const initFilterProps: InitFilterProps = ({
  globalFilter,
  setGlobalFilter,
}) => ({
  getFilteredRowModel: getFilteredRowModel(),
  onGlobalFilterChange: setGlobalFilter,
  globalFilter,
  globalFilterFn: customGlobalFilter,
  filterFns: {
    customGlobalFilter,
    fuzzyFilter: () => true,
  },
});

/**
 * Formats a number to display in K format for thousands
 * Example: 10000 -> 10K, 1500 -> 1.5K
 * @param value The number to format
 * @param decimals The number of decimal places (default: 1)
 * @returns Formatted string with K suffix for thousands
 */
export const formatToK = (value: number, decimals: number = 1): string => {
  if (value < 1000) {
    return value.toString();
  }
  return (value / 1000).toFixed(decimals).replace(/\.0$/, "") + "K";
};

export const CLIENT_TRACKING_DATE_FORMAT = "YYYY-MM-DD";

/**
 * util for formatting date for client tracking segments
 * @param date Date to format
 * @returns Formatted date string in YYYY-MM-DD format
 * If date is undefined or null, returns an empty string
 *  */
export const formatClientTrackingDate = (
  date?: Date | string | null,
  format: string = CLIENT_TRACKING_DATE_FORMAT,
): string => {
  if (!date) {
    return "";
  }
  return dayjs(date).format(format);
};

// Create a canvas for text measurement
export const measureTextWidth = (
  text: string,
  font = "14px Inter, sans-serif",
) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (context) {
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }
  return 0;
};
